import {
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    input,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DsListItemComponent } from '../ds-list/ds-list-item/ds-list-item.component';
import { DsContextMenuTrigger } from './ds-context-menu.types';

@Component({
    selector: 'ds-context-menu',
    imports: [CommonModule],
    templateUrl: './ds-context-menu.component.html',
    styleUrl: './ds-context-menu.component.scss',
    exportAs: 'dsContextMenu',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        tabindex: '-1',
    }
})
export class DsContextMenuComponent {
    /**
     * Template reference to the menu content.
     * Used by the directive to attach the menu to the CDK overlay portal.
     * @internal
     */
    @ViewChild(TemplateRef) templateRef!: TemplateRef<unknown>;

    /**
     * Query list of all list items within the menu (including nested items).
     * Used for keyboard navigation and focus management.
     */
    @ContentChildren(DsListItemComponent, { descendants: true }) listItems!: QueryList<DsListItemComponent>;

    /**
     * Internal flag that could be used to control autoFocus behavior.
     * Currently not actively used but kept for potential future enhancements.
     * @internal
     */
    autoFocus = input<boolean>(false);

    /**
     * Reference to the trigger button element that opened this menu.
     * When this menu is a submenu, stores the parent menu item that triggered it.
     * Used to restore focus to the parent item when closing the submenu with Escape.
     * Set by the directive when the menu opens.
     * @internal
     */
    parentTriggerElement: HTMLElement | null = null;

    /**
     * Direct reference to the trigger directive that opened this menu.
     * Set by the directive in openMenu, cleared in closeMenu.
     * Used to close the menu without a synthetic DOM event.
     * @internal
     */
    triggerDirective: DsContextMenuTrigger | null = null;

    /**
     * Lifecycle callback invoked by the directive when the menu opens or closes.
     * Reserved for future lifecycle hooks; focus management is handled by the
     * directive calling focusFirstItem() directly after openMenu().
     */
    onMenuOpened(_isOpen: boolean, _eventType?: string) {}

    /**
     * Programmatically focuses the first enabled menu item.
     * Uses requestAnimationFrame to wait for the next browser paint cycle,
     * ensuring the overlay DOM is fully rendered before attempting to focus.
     *
     * This is necessary because:
     * 1. The menu is rendered in a CDK overlay portal (outside normal DOM flow)
     * 2. Angular's change detection may not have completed DOM updates yet
     * 3. Focus can only be set on elements that are fully rendered and painted
     *
     * Note: List items have tabindex="-1" by default (arrow key navigation mode),
     * which makes them focusable programmatically but not via Tab key.
     */
    focusFirstItem(): void {
        // Wait for next animation frame (~16ms) when DOM is painted
        requestAnimationFrame(() => {
            if (!this.listItems) {
                return;
            }

            const items = this.listItems.toArray();

            // Find first non-disabled item to focus
            const firstEnabledItem = items.find((item) => !item.disabled);

            if (firstEnabledItem) {
                // Call the FocusableOption interface method
                // Works because list items have tabindex="-1" (programmatically focusable)
                firstEnabledItem.focus();
            }
        });
    }

    /**
     * Handles keyboard events dispatched from the overlay.
     * Called by the trigger directive which attaches a keydown listener on the overlay element.
     * This ensures keyboard events are captured even though the menu content is rendered
     * in a CDK overlay portal (outside the ds-context-menu host element's DOM tree).
     *
     * @param event - The keyboard event from the overlay
     */
    handleKeydown(event: KeyboardEvent): void {
        switch (event.code) {
            case 'ArrowRight':
                event.preventDefault();
                event.stopPropagation();
                this.openSubmenuOnArrowRight();
                break;

            case 'ArrowLeft':
                event.preventDefault();
                event.stopPropagation();
                this.closeSubmenuOnArrowLeft();
                break;
        }
    }

    /**
     * Handles click events on the overlay, fired for both mouse clicks and
     * programmatic .click() calls (e.g. from ds-list's Enter key handling).
     * Closes the menu when a non-disabled, non-submenu ds-list-item is clicked.
     *
     * @param event - The mouse event from the overlay
     * @internal Called by the trigger directive's overlay click listener.
     */
    handleOverlayClick(event: MouseEvent): void {
        const target = (event.target as HTMLElement)?.closest('ds-list-item');
        if (!target) {
            return;
        }

        // Don't close for submenu triggers — the directive opens the submenu
        if (target.classList.contains('ds-context-menu-trigger')) {
            return;
        }

        // Don't close for disabled items
        if (target.getAttribute('disabled') !== null) {
            return;
        }

        this.closeMenu();
    }

    /**
     * Opens a submenu when ArrowRight is pressed on a menu item that has a submenu trigger.
     *
     * Checks if the currently focused element is a ds-list-item with a
     * trigger directive attached (identified by the 'ds-context-menu-trigger' class).
     * If so, dispatches a synthetic ArrowRight keyboard event to trigger the directive's handler.
     *
     * This approach maintains separation of concerns:
     * - The component doesn't need direct access to directive instances
     * - The directive handles all menu opening logic via its event listeners
     * - Communication happens through standard DOM events
     */
    openSubmenuOnArrowRight(): void {
        const activeElement = document.activeElement as HTMLElement;

        // Verify we're focused on a list item
        if (activeElement?.tagName.toLowerCase() !== 'ds-list-item') {
            return;
        }

        // Check if this list item has a context menu trigger directive attached
        // The directive adds 'ds-context-menu-trigger' class to its host element
        if (activeElement.classList.contains('ds-context-menu-trigger')) {
            // Dispatch synthetic keyboard event that the directive will handle
            // This triggers the directive's handleArrowRight via the host listener
            activeElement.dispatchEvent(
                new KeyboardEvent('keydown', {
                    key: 'ArrowRight',
                    code: 'ArrowRight',
                    bubbles: true,
                    cancelable: true,
                })
            );
        }
    }

    /**
     * Closes this menu (acting as a submenu) when ArrowLeft is pressed,
     * restoring focus to the parent menu item that triggered this submenu.
     *
     * Only acts when this menu is a submenu (has a parentTriggerElement).
     * Delegates to the trigger directive's closeMenu with restoreFocus=true.
     */
    closeSubmenuOnArrowLeft(): void {
        // Only close if this is a submenu (has a parent trigger element)
        if (!this.parentTriggerElement) {
            return;
        }

        this.triggerDirective?.closeMenu(true, 'arrow-left');
    }

    /**
     * Programmatically closes the menu via the trigger directive.
     *
     * Called from template bindings like `(click)="menu.closeMenu()"` or
     * `(keydown.enter)="menu.closeMenu()"` on ds-list-items.
     *
     * Uses setTimeout to defer the actual close to the next macrotask. This is
     * necessary because the close triggers overlay detachment which removes the
     * ds-list DOM. If done synchronously during a keydown event, the still-bubbling
     * event would reach ds-list's onKeydown handler after the DOM is torn down,
     * causing errors when it tries to access detached list items.
     */
    closeMenu(): void {
        // Defer to next macrotask so the current event finishes propagating
        // through the (still-attached) DOM before the overlay is detached.
        setTimeout(() => {
            this.triggerDirective?.closeMenu(false, 'item-click');
        });
    }
}